/* eslint-disable no-param-reassign */
// mapbox-gl-draw modes work by mutating the state and display features they
// are handed; that is the contract, not an accident.
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { Feature } from 'geojson';
import { circle, distance, point } from '@turf/turf';
import createSupplementaryPointsForCircle from './createSupplementaryPointsForCircle';

const { constants: Constants, lib } = MapboxDraw;

// A copy, not the library's own direct_select: the plugin this replaces
// mutated the shared built-in mode at import time.
const DirectSelectMode: MapboxDraw.DrawCustomMode = {
  ...MapboxDraw.modes.direct_select,

  dragFeature(
    this: MapboxDraw.DrawCustomModeThis,
    state: any,
    e: MapboxDraw.MapMouseEvent,
    delta: any
  ) {
    lib.moveFeatures(this.getSelected(), delta);
    // The stored centre has to travel with the polygon or the next resize
    // would grow the circle from where it used to be.
    this.getSelected()
      .filter(feature => feature.properties?.isCircle)
      .map(feature => feature.properties?.center)
      .forEach(center => {
        center[0] += delta.lng;
        center[1] += delta.lat;
      });
    state.dragMoveLocation = e.lngLat;
  },

  dragVertex(
    this: MapboxDraw.DrawCustomModeThis,
    state: any,
    e: MapboxDraw.MapMouseEvent,
    delta: any
  ) {
    if (state.feature.properties.isCircle) {
      const { center } = state.feature.properties;
      const movedVertex = [e.lngLat.lng, e.lngLat.lat];
      const radius = distance(point(center), point(movedVertex), {
        units: 'kilometers'
      });
      state.feature.incomingCoords(circle(center, radius).geometry.coordinates);
      state.feature.properties.radiusInKm = radius;
      return;
    }

    const selectedCoords = state.selectedCoordPaths.map((coordPath: string) =>
      state.feature.getCoordinate(coordPath)
    );
    const selectedCoordPoints = selectedCoords.map((coords: number[]) => ({
      type: Constants.geojsonTypes.FEATURE,
      properties: {},
      geometry: { type: Constants.geojsonTypes.POINT, coordinates: coords }
    }));
    const constrainedDelta = lib.constrainFeatureMovement(
      selectedCoordPoints,
      delta
    );
    selectedCoords.forEach((coord: number[], index: number) => {
      state.feature.updateCoordinate(
        state.selectedCoordPaths[index],
        coord[0] + constrainedDelta.lng,
        coord[1] + constrainedDelta.lat
      );
    });
  },

  toDisplayFeatures(state: any, geojson: Feature, push) {
    if (state.featureId === geojson.properties?.id) {
      geojson.properties = {
        ...geojson.properties,
        active: Constants.activeStates.ACTIVE
      };
      push(geojson);
      const supplementaryOptions = {
        map: this.map,
        midpoints: true,
        selectedPaths: state.selectedCoordPaths
      };
      const supplementaryPoints = geojson.properties.user_isCircle
        ? createSupplementaryPointsForCircle(geojson)
        : lib.createSupplementaryPoints(geojson, supplementaryOptions);
      supplementaryPoints?.forEach(push);
    } else {
      geojson.properties = {
        ...geojson.properties,
        active: Constants.activeStates.INACTIVE
      };
      push(geojson);
    }
    (this as any).fireActionable(state);
  }
} as MapboxDraw.DrawCustomMode;

export default DirectSelectMode;
